import { useMemo } from "react";
import { isUndefined, map, reject, sortBy } from "lodash";
import { DateMath, PeriodRange } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Platform } from "@linkfluence/models/platform";
import { natural } from "@tootools/sort";
import { useFacetValuesFromClause } from "@radarly/core/clause/use-cases";
import { useFeature } from "@radarly/core/features/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { Clause } from "@radarly/models/clause";
import { FullFacet, Value, getFacetValueLabel } from "@radarly/models/facets";
import { Choice } from "@radarly/models/filters";
import { getStatus, orderChoices } from "./utils";

const getPlatformsValues = (values) => reject(values, ["id", Platform.Youtube]);

const getChoiceId = (value: string | number, facet: string) => {
  if (facet === Facet.MaritalStatus) {
    return value === "relationship" ? "inRelationship" : "hasChildren";
  }
  return value;
};

const formatChoices = (
  values: Value[],
  facet: FullFacet,
  clause: Clause,
  order: Value[]
): Choice[] => {
  const ordering = orderChoices(order);
  return map(values, (value) => {
    const id = getChoiceId(value.id, facet.id);
    return {
      id,
      label: getFacetValueLabel(facet.id, value.label),
      status: getStatus(id, clause, facet),
      category: value.category,
      position: !isUndefined(ordering[value.id])
        ? ordering[value.id]
        : order.length + 1,
    };
  });
};

export function useFilterbarChoices(
  clause: Clause,
  facet: FullFacet,
  period: PeriodRange = { from: DateMath.Last_30_Days, to: DateMath.Now }
) {
  const project = useCurrentProject();
  //warning: do not activate this feature on insight pages unless the youtube integration has been specified by the product team
  const isYoutubeAllowed = useFeature("youtube_platform");

  const { loading, values, order, error } = useFacetValuesFromClause(
    project.id,
    clause,
    facet,
    period
  );

  const _values = useMemo(() => {
    if (facet.id === Facet.Platforms && !isYoutubeAllowed) {
      return getPlatformsValues(values);
    }
    if (facet.id === Facet.MaritalStatus) {
      return facet.values;
    }
    if (facet.id === Facet.Countries) {
      return natural(values, "label");
    }
    return values;
  }, [facet.id, facet.values, isYoutubeAllowed, values]);

  const choices = useMemo(() => {
    // @TODO: Replace when youtube integration
    return formatChoices(_values, facet, clause, order);
  }, [_values, clause, facet, order]);
  return {
    loading,
    error,
    choices,
    orderedChoices: sortBy(choices, "position"),
  };
}
